import { WIDGET_STYLES } from './styles.js';
import { MARKER_ICON_SVG } from './icon.js';
import { EXIT_SVG } from './widgetDomShared.js';

export interface WidgetDomRefs {
  commentBox: HTMLDivElement;
  exitBtn: HTMLButtonElement;
  highlight: HTMLDivElement;
  hint: HTMLDivElement;
  modalBackdrop: HTMLDivElement;
  nameModal: HTMLDivElement;
  nameOverlay: HTMLDivElement;
  pinTooltip: HTMLDivElement;
  pinsContainer: HTMLDivElement;
  successToast: HTMLDivElement;
  triggerBtn: HTMLButtonElement;
  triggerTooltip: HTMLDivElement;
  triggerWrap: HTMLDivElement;
  viewportBorder: HTMLDivElement;
}

export function appendWidgetShell(options: {
  onExitClick(): void;
  onTriggerClick(): void;
  position: 'bottom-right' | 'bottom-left';
  shadow: ShadowRoot;
}): WidgetDomRefs {
  const { onExitClick, onTriggerClick, position, shadow } = options;
  const style = document.createElement('style');
  style.textContent = WIDGET_STYLES;

  const triggerWrap = document.createElement('div');
  triggerWrap.className = 'bs-trigger-wrap';
  if (position === 'bottom-left') triggerWrap.classList.add('bs-trigger-wrap--left');

  const triggerBtn = createTriggerButton(onTriggerClick);
  const triggerTooltip = createDiv('bs-trigger-tooltip', 'Comments disabled');
  triggerTooltip.hidden = true;

  triggerWrap.append(triggerBtn, triggerTooltip);

  const viewportBorder = createDiv('bs-viewport-border');
  const highlight = createDiv('bs-highlight');
  const hint = createDiv('bs-hint', 'Click anywhere to comment · Press ESC to exit comment mode');
  const exitBtn = document.createElement('button');
  exitBtn.className = 'bs-exit-btn';
  exitBtn.setAttribute('aria-label', 'Exit comment mode (ESC)');
  exitBtn.title = 'Exit comment mode (ESC)';
  exitBtn.innerHTML = EXIT_SVG;
  exitBtn.addEventListener('click', onExitClick);

  const pinsContainer = createDiv();
  pinsContainer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483645;display:none;';

  const pinTooltip = createDiv('bs-pin-tooltip');
  const modalBackdrop = createDiv('bs-modal-backdrop');
  const commentBox = createDiv('bs-comment-box');
  commentBox.addEventListener('click', (event) => event.stopPropagation());
  const nameOverlay = createDiv('bs-name-overlay');
  const nameModal = createDiv('bs-name-modal');
  const successToast = createSuccessToast();

  shadow.append(
    style, triggerWrap, viewportBorder, highlight, hint, exitBtn,
    pinsContainer, pinTooltip, modalBackdrop, commentBox, nameOverlay, nameModal,
    successToast,
  );

  return {
    commentBox,
    exitBtn,
    highlight,
    hint,
    modalBackdrop,
    nameModal,
    nameOverlay,
    pinTooltip,
    pinsContainer,
    successToast,
    triggerBtn,
    triggerTooltip,
    triggerWrap,
    viewportBorder,
  };
}

function createTriggerButton(onTriggerClick: () => void) {
  const button = document.createElement('button');
  button.className = 'bs-trigger';
  button.setAttribute('aria-label', 'Leave feedback');
  button.innerHTML = MARKER_ICON_SVG;
  button.addEventListener('click', onTriggerClick);
  return button;
}

function createDiv(className?: string, textContent?: string) {
  const element = document.createElement('div');
  if (className) element.className = className;
  if (textContent) element.textContent = textContent;
  return element;
}

function createSuccessToast() {
  const toast = createDiv('bs-toast-container');
  toast.innerHTML = `
    <div class="bs-toast bs-toast--success" role="alert">
      <div class="bs-toast-body">
        <span class="bs-toast-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 0a12 12 0 1012 12A12.014 12.014 0 0012 0zm6.927 8.2l-6.845 9.289a1.011 1.011 0 01-1.43.188l-4.888-3.908a1 1 0 111.25-1.562l4.076 3.261 6.227-8.451a1 1 0 111.61 1.183z"/></svg>
        </span>
        <span class="bs-toast-message">Feedback submitted!</span>
      </div>
      <button class="bs-toast-close" type="button" aria-label="close">
        <svg aria-hidden="true" viewBox="0 0 14 16"><path fill-rule="evenodd" d="M7.71 8.23l3.75 3.75-1.48 1.48-3.75-3.75-3.75 3.75L1 11.98l3.75-3.75L1 4.48 2.48 3l3.75 3.75L9.98 3l1.48 1.48-3.75 3.75z"/></svg>
      </button>
      <div class="bs-toast-progress" role="progressbar" aria-label="notification timer" aria-hidden="true"></div>
    </div>`;
  const closeToast = () => {
    toast.style.display = 'none';
    toast.classList.remove('is-visible');
  };
  toast.querySelector('.bs-toast')?.addEventListener('click', closeToast);
  toast.querySelector('.bs-toast-close')?.addEventListener('click', (event) => {
    event.stopPropagation();
    closeToast();
  });
  return toast;
}
